import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Variable, Camera, Sparkles, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";

const URL_BASE = "https://www.inquira.online";
const CANONICAL = `${URL_BASE}/algebra-ai-solver`;

const FAQS = [
  {
    q: "Can Inquira solve algebra problems step by step?",
    a: "Yes. Every algebra problem is broken into labeled steps — isolate the variable, factor, apply the quadratic formula, check the solution — so you learn the method instead of copying an answer.",
  },
  {
    q: "What kinds of algebra can the AI solver handle?",
    a: "Linear equations and inequalities, quadratics (factoring, completing the square, quadratic formula), systems of equations (substitution, elimination, matrices), polynomials, rational and radical expressions, exponents and logarithms, absolute value, and word problems.",
  },
  {
    q: "Can I take a photo of a handwritten algebra problem?",
    a: "Yes. Snap a photo, crop and rotate it, and Inquira's OCR extracts the equation — including handwritten notation — before solving. You can edit the extracted text before it is solved.",
  },
  {
    q: "Does it show how to check the answer?",
    a: "Yes. Solutions end with a verification step that substitutes the result back into the original equation, and you can ask follow-up questions on any individual step.",
  },
  {
    q: "Is the algebra solver free?",
    a: "Free Forever includes 3 AI tutor questions per UTC day and one complimentary homework image scan for eligible new accounts. New Premium terms include 50 daily AI tutor questions and full study access for $9.99/month. Existing subscriber benefits are preserved.",
  },
];

const TOPICS = [
  {
    title: "Linear equations",
    examples: ["3x + 7 = 22", "2(x − 4) = 5x + 1", "Solve for y: 4x − 2y = 10"],
  },
  {
    title: "Quadratics",
    examples: ["x² − 5x + 6 = 0 (factoring)", "2x² + 3x − 5 = 0 (formula)", "Complete the square: x² + 6x = 7"],
  },
  {
    title: "Systems of equations",
    examples: ["x + y = 10, x − y = 4", "Elimination: 3x + 2y = 12, x − 2y = 4", "3×3 systems by matrices"],
  },
  {
    title: "Exponents & logs",
    examples: ["Solve 2ˣ = 32", "log₃(x + 1) = 4", "Simplify (x⁵y²)³ / x⁴"],
  },
];

export const Route = createFileRoute("/algebra-ai-solver")({
  component: AlgebraAiSolverPage,
  head: () => ({
    meta: [
      { title: "Algebra AI Solver — Step-by-Step Solutions | Inquira" },
      {
        name: "description",
        content:
          "Free algebra AI solver with step-by-step explanations for linear equations, quadratics, systems of equations, and word problems. Snap a photo or type a problem.",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Algebra AI Solver — Step-by-Step Solutions" },
      {
        property: "og:description",
        content:
          "Solve linear equations, quadratics, and systems of equations with an AI tutor that shows every step. Photo scan, voice explanations, and practice problems.",
      },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Algebra AI Solver — Step-by-Step Solutions" },
      {
        name: "twitter:description",
        content: "AI algebra solver with worked steps for linear equations, quadratics, and systems of equations.",
      },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${URL_BASE}/` },
            { "@type": "ListItem", position: 2, name: "Algebra AI Solver", item: CANONICAL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
});

const WORKED_EXAMPLES = [
  {
    problem: "x² − 5x + 6 = 0",
    steps: [
      "Recognize a quadratic in standard form ax² + bx + c = 0 with a = 1, b = −5, c = 6.",
      "Look for two numbers that multiply to 6 and add to −5: −2 and −3.",
      "Factor: (x − 2)(x − 3) = 0.",
      "Apply the zero-product property: x − 2 = 0 or x − 3 = 0.",
      "Solutions: x = 2 and x = 3. Check: 2² − 5(2) + 6 = 0 ✓",
    ],
  },
  {
    problem: "3x + 7 = 22",
    steps: [
      "Isolate the variable term by subtracting 7 from both sides: 3x = 15.",
      "Divide both sides by 3: x = 5.",
      "Check: 3(5) + 7 = 22 ✓",
    ],
  },
  {
    problem: "x + y = 10 and x − y = 4",
    steps: [
      "Add the two equations to eliminate y: 2x = 14.",
      "Solve for x: x = 7.",
      "Substitute back into x + y = 10: 7 + y = 10, so y = 3.",
      "Solution: (x, y) = (7, 3). Check both equations ✓",
    ],
  },
];

function AlgebraAiSolverPage() {
  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" aria-label="Back to home">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Variable className="w-5 h-5 text-primary" />
            <span className="font-semibold">Algebra AI Solver</span>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
          Algebra AI Solver with Step-by-Step Solutions
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
          Linear equations, quadratics, systems of equations, and word problems — solved with worked steps you
          can actually learn from. Snap a photo of your problem or type it in.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/">
            <Button size="lg" className="gap-2">
              <Camera className="w-4 h-4" /> Solve a problem
            </Button>
          </Link>
          <Link to="/upgrade">
            <Button size="lg" variant="outline" className="gap-2">
              <Sparkles className="w-4 h-4" /> Get Pro
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">What Inquira can solve</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {TOPICS.map((t) => (
            <div key={t.title} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-3">{t.title}</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {t.examples.map((ex) => (
                  <li key={ex} className="font-mono">• {ex}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">Worked examples</h2>
        <div className="space-y-4">
          {WORKED_EXAMPLES.map(({ problem, steps }) => (
            <div key={problem} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-mono font-semibold mb-3">{problem}</h3>
              <ol className="space-y-2 text-sm text-muted-foreground list-decimal pl-5">
                {steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">How it works</h2>
        <ol className="space-y-4">
          {[
            {
              icon: Camera,
              title: "Capture or type",
              body: "Snap a photo of a textbook or handwritten problem, or type an equation directly.",
            },
            {
              icon: Variable,
              title: "Get worked steps",
              body: "Each step names the move used — isolate, factor, substitute, eliminate — so you learn the method.",
            },
            {
              icon: Mic,
              title: "Listen (Pro)",
              body: "Hear a natural voice narration of the solution with synchronized step highlighting.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <li key={title} className="flex gap-4 rounded-xl border border-border bg-card p-5">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {FAQS.map(({ q, a }) => (
            <div key={q} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-2">{q}</h3>
              <p className="text-sm text-muted-foreground">{a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-4">Studying more than algebra?</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Inquira covers the rest of your coursework too.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/calculus-ai-solver">
            <Button variant="outline">Calculus AI Solver</Button>
          </Link>
          <Link to="/practice">
            <Button variant="outline">Practice mode</Button>
          </Link>
          <Link to="/flashcards">
            <Button variant="outline">Flashcards</Button>
          </Link>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="rounded-2xl border border-border bg-card p-8">
          <h2 className="text-2xl font-bold mb-2">Ready to solve your first equation?</h2>
          <p className="text-muted-foreground mb-6">Free — no credit card required.</p>
          <Link to="/">
            <Button size="lg" className="gap-2">
              <Camera className="w-4 h-4" /> Try Inquira now
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}